import { Component, OnInit, OnDestroy, Input, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { AuthenticationService } from 'src/app/core/authentication/authentication.service';
import { ChatService } from 'src/app/core/service/chat.service';
import { UserService } from 'src/app/core/service/user.service';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { pinIcon, unpinIcon } from '@progress/kendo-svg-icons';
import { sortBy } from 'lodash';
import { LocalStorageService, SessionStorageService } from 'ngx-webstorage';
import { Subscription } from 'rxjs';
import { MessageSubject, MessengerService } from 'src/app/core/service/messenger.service';
import { ChatUserSelectionEdit } from '../chat-user-selection/model/chat-user-selection';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-chat-list',
  templateUrl: './chat-list.component.html',
  styleUrls: ['./chat-list.component.scss']
})
export class ChatListComponent implements OnInit, OnDestroy, AfterViewInit
{
  @Input() public windowHeight: number;
  @Input() public isWindow = false;

  @ViewChild('splitterVer', { read: ElementRef }) public splitterVer: ElementRef;
  @ViewChild('splitterHor', { read: ElementRef }) public splitterHor: ElementRef;

  public allChat = [];
  public selectedChat = null;
  public chatCollapsed = false;
  public spaceCollapsed = false;
  public spaceEnabled = true;
  public users = [];
  public activeUsers = [];
  public userSelectionOpened = false;
  public createMode: string | 'CHAT' | 'SPACE';
  public icons = { pin: pinIcon, unpin: unpinIcon };
  public leaveDialogOpened = false;
  public leaveChatSelected = undefined;
  public skipReloadChatHistory = false;
  public isNew = false;
  public editChatItem: ChatUserSelectionEdit;
  public leaveChatType = '';

  private messagingSubscription: Subscription;
  private chatHorizontalPaneSize = '50vh';
  private chatVerticalPaneSize = '20%';

  constructor(
    private translate: TranslateService,
    private chatService: ChatService,
    private storageService: LocalStorageService,
    private sessionStorage: SessionStorageService,
    private authenticationService: AuthenticationService,
    private messageHandlerService: MessageHandlerService,
    private messengerService: MessengerService,
    private userService: UserService)
  {
    this.chatHorizontalPaneSize = this.storageService.retrieve('chat-horizontal-pane-size') || '50vh';
    this.chatVerticalPaneSize = this.storageService.retrieve('chat-vertical-pane-size') || '20%';
    this.messagingSubscription = this.messengerService.receiveMessage().subscribe({
      next: (message) =>
      {
        if (message && message.type)
        {
          // Update the chat list
          if (message.type === 'wsChatCount' || message.type === 'refreshChatUnread')
          {
            const responseMessage = message.type === 'wsChatCount' ? JSON.parse(message.data.body) : message.data;
            responseMessage.forEach((m: any) =>
            {
              const chatFound = this.allChat.find(x => x.id === m.chatId);
              if (chatFound)
              {
                // Reload history if the chat is the current selected and have changed
                if (this.selectedChat && this.selectedChat.id === chatFound.id)
                {
                  // This is the callback from chat history, to simply update the unread
                  if (message.type === 'refreshChatUnread')
                  {
                    const chatUser = this.selectedChat.users.find((u: any) => u.userId === this.authenticationService.currentUser.id);
                    this.chatService.setUnread(chatUser.id, 'false', false).subscribe({
                      next: () =>
                      {
                        chatFound.unread = m.unreadCount > 0;
                        this.messengerService.sendMessage(new MessageSubject('reloadSideNavChatCount'));
                      }
                    });
                  }
                  else
                  {
                    // We must reload the chat
                    if (!this.skipReloadChatHistory)
                    {
                      this.messengerService.sendMessage(new MessageSubject('reloadChatHistory', {
                        chatId: chatFound.id,
                        unreadCount: m.unreadCount
                      }));
                    }
                  }
                }
                else
                {
                  chatFound.unread = m.unreadCount > 0;
                }
              }
            });
          }
        }
      }
    });
  }

  public get chatList(): any[]
  {
    return this.allChat.filter(x => x.chatSpace === null);
  }

  public get spaceList(): any[]
  {
    return this.allChat.filter(x => x.chatSpace !== null);
  }

  public get horizontalPaneSize(): string
  {
    return this.chatHorizontalPaneSize;
  }

  public set horizontalPaneSize(newSize: string)
  {
    this.chatHorizontalPaneSize = newSize;
    this.storageService.store('chat-horizontal-pane-size', newSize);
  }

  // eslint-disable-next-line @typescript-eslint/member-ordering
  public get verticalPaneSize(): string
  {
    return this.chatVerticalPaneSize;
  }

  public set verticalPaneSize(newSize: string)
  {
    this.chatVerticalPaneSize = newSize;
    this.storageService.store('chat-vertical-pane-size', newSize);
  }

  public ngOnInit(): void
  {
    this.userService.getCachedUsers(false, false, null, true).subscribe({
      next: (data) =>
      {
        this.users = data;
        this.activeUsers = sortBy(data.filter(x => x.enabled), ['name']);
        this.loadChats();
      },
      error: (errorResponse: any) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  public ngAfterViewInit(): void
  {
    this.updateSplitterBars();
  }

  public ngOnDestroy(): void
  {
    if (this.messagingSubscription !== undefined) { this.messagingSubscription.unsubscribe(); }
    this.saveSelectedChat(null);
  }

  /**
   * Open new Chat or Space dialog
   *
   * @param mode Creation mode : CHAT or SPACE
   */
  public new(mode: string): void
  {
    this.createMode = mode;
    this.isNew = true;
    const editChatItem = new ChatUserSelectionEdit();
    editChatItem.users.push({ id: this.authenticationService.currentUser.id, name: this.authenticationService.currentUser.name });
    this.editChatItem = editChatItem;
    this.userSelectionOpened = true;
  }

  /**
   * Cancel new Chat or Space dialog
   */
  public cancel(): void
  {
    this.userSelectionOpened = false;
    this.createMode = null;
  }

  /**
   * Create a new Chat or Space
   *
   * @param selected Selected space name/description and selected users.
   */
  public saveChat(selected: any): void
  {
    this.userSelectionOpened = false;
    this.chatService.updateChat(selected, this.isNew, selected.chatId).subscribe({
      next: (data) =>
      {
        const txtKey = this.isNew ? 'ADDED' : 'UPDATED';
        this.messageHandlerService.displaySuccess(`${this.createMode}-${txtKey}-SUCCESFULLY`);

        const foundChat = this.allChat.find(x => x.id === data.id);
        const chatObject = foundChat || data;
        chatObject.text = this.getChatText(data);
        chatObject.chatSpace = data.chatSpace;
        chatObject.users = data.users;
        if (!foundChat)
        {
          this.allChat.push(chatObject);
        }

        // Select the chat or space
        this.chatSelected(chatObject.id, true);
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  /**
   * Set the selected chat based for the chat id provided
   *
   * @param id Chat id to select.
   */
  public chatSelected(id: number, isNew: boolean): void
  {
    this.selectedChat = this.allChat.find(x => x.id === id);
    this.saveSelectedChat(this.selectedChat);

    if (!isNew && this.selectedChat && this.selectedChat.unread)
    {
      const chatUser = this.selectedChat.users.find((u: any) => u.userId === this.authenticationService.currentUser.id);
      this.chatService.setUnread(chatUser.id, 'false').subscribe({
        next: () =>
        {
          this.selectedChat.unread = false;
        }
      });
    }
  }

  /**
   * Return true if the are chats in the list
   */
  public hasChats(): boolean
  {
    return this.allChat.filter(x => x.chatSpace === null).length > 0;
  }

  /**
   * Return true if there are spaces in the list
   */
  public hasSpaces(): boolean
  {
    return this.allChat.filter(x => x.chatSpace !== null).length > 0;
  }

  public leaveChat(item: any): void
  {
    this.leaveChatSelected = item;
    this.leaveChatType = this.translate.instant(item.chatSpace ? 'SPACE' : 'CHAT').toLowerCase();
    this.open('leaveDialog');
  }

  public leaveAction(): void
  {
    this.close('leaveDialog');
    const chatUser = this.leaveChatSelected.users.find((u: any) => u.userId === this.authenticationService.currentUser.id);
    this.chatService.leaveChat(chatUser.id).subscribe({
      next: () =>
      {
        this.allChat = this.allChat.filter(x => x.id !== this.leaveChatSelected.id);
        this.selectFirstChatOrSpace();
        this.leaveChatSelected = undefined;
      },
      error: (errorResponse: any) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  public updateUnread(item: any, newUnread: boolean): void
  {
    this.saveSelectedChat(null);  // We must clear the storage to force updating the unread count from left-nav
    this.skipReloadChatHistory = true;
    const chatUser = item.users.find((u: any) => u.userId === this.authenticationService.currentUser.id);
    this.chatService.setUnread(chatUser.id, item.unread ? 'false' : 'true').subscribe({
      next: () =>
      {
        const chatFound = this.allChat.find(x => x.id === item.id);
        if (chatFound)
        {
          chatFound.unread = newUnread;
        }
        this.skipReloadChatHistory = false;
        this.saveSelectedChat(this.selectedChat); // Must put it back to avoid refreshing the left-nav count
      }
    });
  }

  public updatePinned(item: any): void
  {
    const chatUser = item.users.find((u: any) => u.userId === this.authenticationService.currentUser.id);
    this.chatService.setPinned(chatUser.id, item.pinned ? 'false' : 'true').subscribe({
      next: () =>
      {
        item.pinned = !item.pinned;
        this.allChat = sortBy(this.allChat, 'pinned').reverse();
      }
    });
  }

  public toggleCollapsible(chatType: string, value: boolean): void
  {
    if (chatType === 'CHAT')
    {
      this.chatCollapsed = value;
    }
    else
    {
      this.spaceCollapsed = value;
    }
  }

  public manageChatOrSpace(item: any, turnToSpace = false): void
  {
    this.chatService.getChatById(item.id).subscribe({
      next: (data: any) =>
      {
        this.isNew = false;
        this.createMode = item.chatSpace || turnToSpace ? 'SPACE' : 'CHAT';

        const chatObject = new ChatUserSelectionEdit();
        chatObject.chatId = data.id;
        chatObject.spaceId = data.chatSpace ? data.chatSpace.id : null;
        chatObject.spaceName = data.chatSpace ? data.chatSpace.name : null;
        chatObject.spaceDescription = data.chatSpace ? data.chatSpace.description : null;
        chatObject.users = data.users.map((x) => ({ id: x.userId, name: x.name }));

        this.editChatItem = chatObject;
        this.userSelectionOpened = true;
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  /**
   * Closes a component
   *
   * @param component Name of component to be closed.
   */
  public close(component: string): void
  {
    this[component + 'Opened'] = false;
  }

  /**
   * Opens a component
   *
   * @param component Name of component to be opened.
   */
  public open(component: string): void
  {
    this[component + 'Opened'] = true;
  }

  public updateSplitterBars()
  {
    const barVer = this.splitterVer.nativeElement.querySelector('.k-splitbar');
    const barHor = this.splitterHor.nativeElement.querySelectorAll('.k-splitbar')[1];

    if (barVer)
    {
      const pane = this.splitterVer.nativeElement.querySelector('.k-pane');
      const size = pane ? pane.offsetHeight : 0;

      barVer.setAttribute('aria-valuenow', size.toString());
    }
    if (barHor)
    {
      const pane = this.splitterHor.nativeElement.querySelectorAll('.k-pane')[1];
      const size = pane ? pane.offsetWidth : 0;

      barHor.setAttribute('aria-valuenow', size.toString());
    }
  }

  private loadChats(): void
  {
    this.allChat = [];
    this.chatService.getUserChat().subscribe({
      next: (data) =>
      {
        // Set the chat text based on the users or space name
        data.forEach(x =>
        {
          x.text = this.getChatText(x);
          const currentUser = x.users.find(u => u.userId === this.authenticationService.currentUser.id);
          x.unread = currentUser ? currentUser.unread : false;
          x.pinned = currentUser ? currentUser.pinned : false;
        });
        this.allChat = data;

        // Select the first available chat or space
        this.selectFirstChatOrSpace();
      },
      error: (errorResponse: any) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  /**
   * Method that return the chat or space name
   *
   * @param chat Chat object
   * @returns Comma delimited chat users or space name.
   */
  private getChatText(chat: any): string
  {
    // Set the text to the space name if not null
    if (chat.chatSpace === null)
    {
      const userText = [];
      chat.users
        .filter((user: any) => user.userId !== this.authenticationService.currentUser.id)
        .forEach((user: any) => { userText.push(user.firstName); });
      return userText.join(', ');
    }
    else
    {
      return chat.chatSpace.name;
    }
  }

  private selectFirstChatOrSpace(): void
  {
    if (this.chatList.length > 0)
    {
      this.selectedChat = this.chatList[0];
    }
    else if (this.spaceList.length > 0)
    {
      this.selectedChat = this.spaceList[0];
    }
    else
    {
      this.selectedChat = null;
    }
    this.saveSelectedChat(this.selectedChat);
  }

  private saveSelectedChat(selectedChat: any): void
  {
    if (selectedChat)
    {
      this.sessionStorage.store('chat-selected-id', selectedChat.id);
    }
    else
    {
      this.sessionStorage.clear('chat-selected-id');
    }
  }
}
